import {
	ButtonGroup,
	ColumnHeader,
	IPageRange,
	ISelectOption,
	PageTurnOptions,
	PagingHelper,
	PagingOptions,
	SearchBox,
	SearchHelper,
	Select,
	Table,
	ToolMenu,
	ToolMenuContent,
} from '@aon/aon-tws-common-ui';
import { ILibraryQuestion as ICommonLibraryQuestion, IQuestionVariation } from '@aon/tws-common-ui-questions/build/lib/models';
import * as React from 'react';
import styled from 'styled-components';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import { IColumn, IExternalAttributeVm } from '../../../models';
import { ColumnHelper, LibraryQuestionColumnHelper, MappedAttributeHelper, QuestionFilterHelper, SortHelper } from '../../../utilities';
import { NoSearchResultsMessage } from '../../common';
import { DemographicQuestionRow } from './DemographicQuestionRow';

export const TableContainer = styled.div`
	margin: 40px 0;
`;
const ToolMenuButtonGroup = styled(ButtonGroup)`
	width: 100%;
`;
const FilterSelectWrapper = styled.div`
	margin-left: 10px;
	@media (min-width: 0) and (max-width: 768px) {
		margin-left: 0;
		margin-top: 10px;
	}
`;
const PageTurnOptionsContainer = styled.div`
	float: left;
`;

export interface IDemographicQuestionListTableProps {
	questions: ICommonLibraryQuestion[];
	loading: boolean;
	columns: IColumn[];
	availableCultures: ISelectOption[];
	participantAttributes: IExternalAttributeVm[];
	surveySupportedCultures: string[];
	selectedAttributes: string[];
	selectedQuestionVariationIds: number[];
	totalSelectedQuestions: number;
	shouldRemovePaging?: boolean;
	GetCulturesIfNeeded: () => void;
	UpdateColumns: (columns?: IColumn[]) => void;
	SelectQuestion: (question: ICommonLibraryQuestion, isSelected: boolean) => void;
	SelectQuestionVariation: (question: ICommonLibraryQuestion, questionVariation: IQuestionVariation, isSelected: boolean) => void;
	ShowSingleVariation: (questionId: number) => void;
}

type SortableColumns = 'code' | 'dimensions' | 'isSelected' | 'text';

interface ISortParam {
	sortedBy: SortableColumns;
	sortAsc: 'asc' | 'desc';
}

export interface IDemographicQuestionListTableState {
	searchText: string;
	pageRange: IPageRange;
	selectedFilterOptions: string[];
	filterOptions: ISelectOption[];
	sortParams: ISortParam[];
}

export class DemographicQuestionListTable extends React.PureComponent<
	IDemographicQuestionListTableProps,
	IDemographicQuestionListTableState
> {
	constructor(props: IDemographicQuestionListTableProps) {
		super(props);

		this.state = {
			searchText: '',
			pageRange: { offset: 0, limit: 25 },
			selectedFilterOptions: [],
			filterOptions: [],
			sortParams: [{ sortedBy: 'text', sortAsc: 'asc' }],
		};

		this.SortOnColumn = this.SortOnColumn.bind(this);
		this.UpdateSelectedColumns = this.UpdateSelectedColumns.bind(this);
		this.GetSortParam = this.GetSortParam.bind(this);
		this.UpdatePaging = this.UpdatePaging.bind(this);
		this.UpdateSearch = this.UpdateSearch.bind(this);
		this.GetFilteredQuestions = this.GetFilteredQuestions.bind(this);
		this.SortQuestions = this.SortQuestions.bind(this);
		this.GetPropertySelector = this.GetPropertySelector.bind(this);
	}

	public static getDerivedStateFromProps(props: IDemographicQuestionListTableProps, state: IDemographicQuestionListTableState) {
		const filterOptions = DemographicQuestionListTable.GetFilterOptions(props);

		const selectedFilterOptions = state.selectedFilterOptions.filter((selectedValue) => {
			return filterOptions.some((filterOption) => filterOption.name === selectedValue);
		});

		return { filterOptions, selectedFilterOptions };
	}

	public static GetFilterOptions(props: IDemographicQuestionListTableProps) {
		const questions = props.questions;

		if (!questions.length) {
			return [];
		}

		const rogOptions: ISelectOption[] = QuestionFilterHelper.deriveUniqueResponseOptionGroups(questions);
		const tagOptions: ISelectOption[] = QuestionFilterHelper.deriveUniqueTags(questions);
		const templateOptions: ISelectOption[] = QuestionFilterHelper.deriveUniqueQuestionTemplates(questions);
		const languageOptions: ISelectOption[] = QuestionFilterHelper.deriveUniqueCultures(props.availableCultures, props.questions);

		return rogOptions.concat(tagOptions).concat(languageOptions).concat(templateOptions);
	}

	public render() {
		const filteredQuestions = this.GetFilteredQuestions();
		const pageRange = { ...this.state.pageRange, total: filteredQuestions.length };
		const selectableColumns = this.props.columns.filter((c) => c.name !== 'isSelected');
		const { participantAttributes, selectedAttributes, selectedQuestionVariationIds, shouldRemovePaging } = this.props;
		const tableQuestions: ICommonLibraryQuestion[] = shouldRemovePaging
			? filteredQuestions
			: new PagingHelper<ICommonLibraryQuestion>().page(filteredQuestions, this.state.pageRange);

		return (
			<>
				{!shouldRemovePaging && <PagingOptions pageRange={pageRange} updatePageRange={this.UpdatePaging} />}
				<ToolMenu>
					<ToolMenuContent type={'right'}>
						<SearchBox
							callbackFunc={this.UpdateSearch}
							resultsCount={filteredQuestions.length}
							initialSearchText={this.state.searchText}
							inputSize={'sm'}
						/>
					</ToolMenuContent>
					<ToolMenuContent type={'left'}>
						<ToolMenuButtonGroup type={'vertical'}>
							<Select
								items={selectableColumns}
								multiple={true}
								selectedValues={ColumnHelper.GetSelectedColumnNames(selectableColumns)}
								onApply={this.UpdateSelectedColumns}
								showCount={true}
								displayText={ContentConstants.COLUMNPICKER_TITLE}
								selectWidth='180px'
								optionsContainerWidth='250px'
								size='sm'
							/>
							<FilterSelectWrapper>
								<Select
									items={this.state.filterOptions}
									selectedValues={this.state.selectedFilterOptions}
									onApply={(values: any) => this.setState({ selectedFilterOptions: values })}
									hasGroups={true}
									multiple={true}
									showCount={true}
									size='sm'
									displayText='Filter'
								/>
							</FilterSelectWrapper>
						</ToolMenuButtonGroup>
					</ToolMenuContent>
				</ToolMenu>
				<TableContainer>
					<Table responsive={true} selectable={'true'}>
						<ColumnHeader
							sortedBy={this.state.sortParams[0].sortedBy}
							sortAsc={this.state.sortParams[0].sortAsc === 'asc'}
							columns={LibraryQuestionColumnHelper.getColumnHeaders(this.props.columns)}
							handleSort={this.SortOnColumn}
							selectable={false}
						/>
						<tbody>
							{
								// Map each of the questions.
								tableQuestions.map((question: ICommonLibraryQuestion, index) => {
									const mappedAttribute = MappedAttributeHelper.getMappedAttributeForLibraryQuestion(
										question,
										selectedAttributes,
										selectedQuestionVariationIds,
										participantAttributes,
									);
									const isAvailable = MappedAttributeHelper.libraryQuestionIsAvailableForSelection(
										question,
										selectedAttributes,
										selectedQuestionVariationIds,
										participantAttributes,
									);
									return (
										<DemographicQuestionRow
											key={index}
											{...this.props}
											{...this.state}
											question={question}
											disabled={!isAvailable}
											isVariationDisabled={false}
											mappedAttribute={mappedAttribute}
										/>
									);
								})
							}
						</tbody>
					</Table>
					{!filteredQuestions.length && this.state.searchText && <NoSearchResultsMessage />}
					{!shouldRemovePaging && (
						<PageTurnOptionsContainer>
							<PageTurnOptions pageRange={pageRange} updatePageRange={this.UpdatePaging} />
						</PageTurnOptionsContainer>
					)}
				</TableContainer>
			</>
		);
	}

	public SortOnColumn(sortedBy: string, sortAscByDefault: boolean) {
		const primarySortParam = this.state.sortParams[0];
		if (primarySortParam.sortedBy === sortedBy) {
			const copy = [...this.state.sortParams];
			copy[0] = { ...primarySortParam, sortAsc: primarySortParam.sortAsc === 'asc' ? 'desc' : 'asc' };
			this.setState({ sortParams: copy });
		} else {
			const sortParams = [this.GetSortParam(sortedBy, sortAscByDefault)];
			// assign default sort direction of the secondary column back to default.
			const col = this.props.columns.find((c) => c.name === primarySortParam.sortedBy);
			const colDirection = col && col.sortAscByDefault ? col.sortAscByDefault : true;
			sortParams.push(this.GetSortParam(primarySortParam.sortedBy, colDirection));
			this.setState({ sortParams });
		}
	}

	public UpdateSelectedColumns(values?: any) {
		const columns = ColumnHelper.UpdateSelectedColumns(this.props.columns, values.concat('isSelected'));
		this.props.UpdateColumns(columns);
	}

	private GetSortParam(sortedBy: string, sortedByAsc: boolean): ISortParam {
		switch (sortedBy) {
			case 'code':
			case 'dimensions':
			case 'isSelected':
			case 'text':
				return { sortedBy, sortAsc: sortedByAsc ? 'asc' : 'desc' };
			default:
				throw Error('Can not sort on unsupported property: ' + sortedBy);
		}
	}

	private UpdatePaging(pageRange: IPageRange) {
		this.setState({ pageRange });
	}

	private UpdateSearch(searchText: string) {
		this.setState({ searchText, pageRange: { ...this.state.pageRange, offset: 0 } });
	}

	private GetFilteredQuestions() {
		let questions = [...this.props.questions];

		questions = new SearchHelper<ICommonLibraryQuestion>()
			.addColumnSelector((q) => q.text)
			.addColumnSelector((q) => q.code)
			.addColumnSelector((q) => (q.questionVariations || []).map((item) => item.text).join(','))
			.search(questions, this.state.searchText);

		// filter
		const isSelected = (prefix: string, value: string) => {
			return this.state.selectedFilterOptions.indexOf(prefix + value) > -1;
		};

		if (this.state.selectedFilterOptions.some((o) => o.startsWith(Constants.FILTER_PREFIX_TAG))) {
			questions = questions.filter((q) => (q.tagNames || []).some((t) => isSelected(Constants.FILTER_PREFIX_TAG, t)));
		}

		if (this.state.selectedFilterOptions.some((o) => o.startsWith(Constants.FILTER_PREFIX_ROG))) {
			questions = questions.filter((q) => q.responseOptionGroup != null && isSelected(Constants.FILTER_PREFIX_ROG, q.responseOptionGroup));
		}

		if (this.state.selectedFilterOptions.some((o) => o.startsWith(Constants.FILTER_PREFIX_LANGUAGE))) {
			questions = questions.filter((q) => q.languages.some((t) => isSelected(Constants.FILTER_PREFIX_LANGUAGE, t)));
		}

		if (this.state.selectedFilterOptions.some((o) => o.startsWith(Constants.FILTER_PREFIX_QUESTIONTEMPLATE))) {
			questions = questions.filter((q) =>
				(q.templateNames || []).some((t) => isSelected(Constants.FILTER_PREFIX_QUESTIONTEMPLATE, t + '')),
			);
		}

		return this.SortQuestions(questions);
	}

	private SortQuestions(questions: ICommonLibraryQuestion[]) {
		const sortHelper = new SortHelper<ICommonLibraryQuestion>();
		this.state.sortParams.forEach((p) => {
			sortHelper.AddSortProperty(this.GetPropertySelector(p.sortedBy), p.sortAsc);
		});
		return sortHelper.Sort(questions);
	}

	private GetPropertySelector(columnName: SortableColumns) {
		switch (columnName) {
			case 'text':
				return (q: ICommonLibraryQuestion) => q.text;
			case 'code':
				return (q: ICommonLibraryQuestion) => q.code;
			case 'dimensions':
				return (q: ICommonLibraryQuestion) => (q.dimensionNames || []).join(',');
			case 'isSelected':
				return (q: ICommonLibraryQuestion) =>
					!(q.questionVariationId && this.props.selectedQuestionVariationIds.indexOf(q.questionVariationId) > -1) + '';
		}
	}
}
